/**
 * Permisos de módulo y sección — misma regla que el backend
 * (`opciones.module_permissions`). Ausente = none, nunca full.
 */

export type PermissionLevel = "none" | "read" | "full";

/** Claves de sección persistidas en `modulos_permitidos[].sections`. */
export type SectionKey =
  | "configurador"
  | "backups"
  | "identidad"
  | "arca"
  | "abm"
  | "estado_cuenta"
  | "comprobantes"
  | "listado"
  | "importar"
  | "movimientos"
  | "cierre"
  | "pedidos"
  | "remitos"
  | "pagos"
  | "historial"
  | "columna_total"
  | "crear_proveedor"
  | "facturas"
  | "remitos_vtas"
  | "remitos_compras"
  | "estadisticas";

export const MODULE_PROVEEDORES = 1;
export const MODULE_CLIENTES = 2;
export const MODULE_VENTAS = 3;
export const MODULE_CHEQUES = 4;
export const MODULE_IVA = 5;
export const MODULE_STOCK = 6;
export const MODULE_PEDIDOS = 7;
export const MODULE_REPORTES = 9;
export const MODULE_CAJA = 10;
export const MODULE_OPCIONES = 11;
export const MODULE_PRESUPUESTOS = 12;
export const MODULE_RECIBOS = 14;
export const MODULE_REMITOS_VTAS = 15;
/** Matriz Roles (cards del Inicio). No es tile del Start Menu. */
export const MODULE_DASHBOARD = 16;

export interface ModulePermissionEntry {
  moduleId: number;
  permission?: PermissionLevel | string;
  sections?: Partial<Record<SectionKey | string, PermissionLevel | string>>;
}

const LEVELS = new Set<PermissionLevel>(["none", "read", "full"]);

function coerceLevel(raw: unknown): PermissionLevel {
  if (typeof raw === "string" && LEVELS.has(raw as PermissionLevel)) {
    return raw as PermissionLevel;
  }
  return "none";
}

/** `remitosVtas` / `remitos-vtas` → `remitos_vtas`. GET camelCase no debe heredar ON. */
export function canonicalSectionKey(key: string): string {
  const hyphenToUnderscore = String(key || "").replace(/-/g, "_");
  return hyphenToUnderscore
    .replace(/([a-z0-9])([A-Z])/g, "$1_$2")
    .replace(/([A-Z])([A-Z][a-z])/g, "$1_$2")
    .toLowerCase();
}

export function sectionKeyAliases(key: string): string[] {
  const raw = String(key || "");
  const snake = canonicalSectionKey(raw);
  const camel = snake.replace(/_([a-z])/g, (_, letter: string) => letter.toUpperCase());
  const hyphen = snake.replace(/_/g, "-");
  return [...new Set([raw, snake, camel, hyphen].filter(Boolean))];
}

function lookupSectionLevel(
  sections: ModulePermissionEntry["sections"] | undefined,
  sectionKey: string,
): PermissionLevel | undefined {
  if (!sections) return undefined;
  for (const alias of sectionKeyAliases(sectionKey)) {
    if (Object.prototype.hasOwnProperty.call(sections, alias)) {
      return coerceLevel(sections[alias]);
    }
  }
  return undefined;
}

/** Alias camel/hyphen → snake. Si hay `none` y otro valor, gana `none` (destild). */
export function canonicalizeSections(
  sections: ModulePermissionEntry["sections"] | undefined | null,
): Record<string, PermissionLevel> {
  const merged: Record<string, PermissionLevel> = {};
  if (!sections) return merged;
  for (const [rawKey, rawLevel] of Object.entries(sections)) {
    const canon = canonicalSectionKey(rawKey);
    const level = coerceLevel(rawLevel);
    merged[canon] = merged[canon] === "none" || level === "none" ? "none" : level;
  }
  return merged;
}

export function canonicalizeModulePermissionEntries<T extends ModulePermissionEntry>(
  list: T[] | undefined | null,
): T[] {
  if (!Array.isArray(list)) return [];
  return list.map((entry) => {
    if (!entry || !entry.sections) return entry;
    return { ...entry, sections: canonicalizeSections(entry.sections) };
  });
}

function entryModuleId(entry: ModulePermissionEntry): number | null {
  const id = entry.moduleId ?? (entry as { module_id?: number }).module_id;
  if (id == null || Number.isNaN(Number(id))) return null;
  return Number(id);
}

export function getModulePermissionLevel(
  modulePermissions: ModulePermissionEntry[] | undefined | null,
  moduleId: number,
): PermissionLevel {
  if (!modulePermissions?.length) return "none";
  const entry = modulePermissions.find((p) => entryModuleId(p) === moduleId);
  if (!entry) return "none";
  return coerceLevel(entry.permission);
}

export function getSectionPermissionLevel(
  modulePermissions: ModulePermissionEntry[] | undefined | null,
  moduleId: number,
  sectionKey: SectionKey | string,
): PermissionLevel {
  const parent = getModulePermissionLevel(modulePermissions, moduleId);
  if (parent === "none") return "none";
  const entry = modulePermissions?.find((p) => entryModuleId(p) === moduleId);
  const found = lookupSectionLevel(entry?.sections, String(sectionKey));
  if (found !== undefined) return found;
  return parent;
}

/** Nivel sin heredar el padre. Ausente → none (flags opt-in como crear_proveedor). */
export function getExplicitSectionPermissionLevel(
  modulePermissions: ModulePermissionEntry[] | undefined | null,
  moduleId: number,
  sectionKey: SectionKey | string,
): PermissionLevel {
  const entry = modulePermissions?.find((p) => entryModuleId(p) === moduleId);
  const found = lookupSectionLevel(entry?.sections, String(sectionKey));
  return found ?? "none";
}

/**
 * Botón «Nuevo proveedor» en el personalizado (Otras).
 * Flag Ventas.crear_proveedor (opt-in) o ABM Proveedores Completo.
 * Lookup solo no alcanza. Elevado → sí.
 */
export function canCreateProveedorFromPersonalizado(
  modulePermissions: ModulePermissionEntry[] | undefined | null,
  opts?: { isElevated?: boolean },
): boolean {
  if (opts?.isElevated) return true;
  if (getSectionPermissionLevel(modulePermissions, MODULE_PROVEEDORES, "abm") === "full") {
    return true;
  }
  return (
    getExplicitSectionPermissionLevel(
      modulePermissions,
      MODULE_VENTAS,
      "crear_proveedor",
    ) === "full"
  );
}

export function isModuleReadOnly(
  modulePermissions: ModulePermissionEntry[] | undefined | null,
  moduleId: number,
): boolean {
  return getModulePermissionLevel(modulePermissions, moduleId) !== "full";
}

export function canSeeSection(
  modulePermissions: ModulePermissionEntry[] | undefined | null,
  moduleId: number,
  sectionKey: SectionKey | string,
): boolean {
  return getSectionPermissionLevel(modulePermissions, moduleId, sectionKey) !== "none";
}

/**
 * Usar el wizard en una línea de venta/presupuesto.
 * Ventas o Presupuestos Completo; no exige Stock → Configurador.
 */
export function canUseConfiguradorWizard(
  modulePermissions: ModulePermissionEntry[] | undefined | null,
): boolean {
  return (
    getModulePermissionLevel(modulePermissions, MODULE_VENTAS) === "full" ||
    getModulePermissionLevel(modulePermissions, MODULE_PRESUPUESTOS) === "full"
  );
}

export function isSectionReadOnly(
  modulePermissions: ModulePermissionEntry[] | undefined | null,
  moduleId: number,
  sectionKey: SectionKey | string,
): boolean {
  return getSectionPermissionLevel(modulePermissions, moduleId, sectionKey) !== "full";
}

/** Primera clave visible según el orden del módulo (pestañas). */
export function firstVisibleSection(
  modulePermissions: ModulePermissionEntry[] | undefined | null,
  moduleId: number,
  orderedKeys: string[],
): string | null {
  for (const key of orderedKeys) {
    if (canSeeSection(modulePermissions, moduleId, key)) return key;
  }
  return null;
}

export type SessionModuleAccess = {
  modulosPermitidos?: Array<number | string> | null;
  isStaff?: boolean;
  is_staff?: boolean;
  isSuperuser?: boolean;
  is_superuser?: boolean;
  isElevated?: boolean;
  is_elevated?: boolean;
};

/** is_superuser KEEP, or derived `/me/` `isElevated` (rol.canonico). Not is_staff.
 * Rollback Fase 4 (Fase 7 = git revert, no FLAG_ROLES): volver a OR is_staff.
 * Tras cualquier revert, is_superuser debe seguir short-circuit.
 */
export function sessionIsElevated(
  user: SessionModuleAccess | null | undefined,
): boolean {
  if (!user) return false;
  if (user.isSuperuser || user.is_superuser) return true;
  return Boolean(user.isElevated || user.is_elevated);
}

export function userHasModuleId(
  modulosPermitidos: Array<number | string> | null | undefined,
  moduleId: number,
): boolean {
  if (!modulosPermitidos?.length) return false;
  const want = Number(moduleId);
  return modulosPermitidos.some((id) => Number(id) === want);
}

/** Superuser: todo. Si no, `modulosPermitidos` (tiles /me/). Staff sigue el rol.
 * Lista vacía / ausente = none (fail-closed). Nunca “sin perfil = todos”.
 * Rollback Fase 5 (Fase 7 git revert): no tratar lista vacía como full otra vez.
 */
export function sessionCanAccessModule(
  user: SessionModuleAccess | null | undefined,
  moduleId: number,
): boolean {
  if (!user) return false;
  if (sessionIsElevated(user)) return true;
  return userHasModuleId(user.modulosPermitidos, moduleId);
}

/** GET facturas: Ventas o IVA (mismo extra_read que RequireVentas). */
export function sessionCanFetchFacturas(
  user: SessionModuleAccess | null | undefined,
): boolean {
  return (
    sessionCanAccessModule(user, MODULE_VENTAS) ||
    sessionCanAccessModule(user, MODULE_IVA)
  );
}

export function sessionAccessFingerprint(
  user: SessionModuleAccess | null | undefined,
): string {
  if (!user) return "";
  const elevated = sessionIsElevated(user) ? "e" : "n";
  const mods = [...(user.modulosPermitidos || [])]
    .map((id) => Number(id))
    .filter((id) => Number.isFinite(id))
    .sort((a, b) => a - b)
    .join(",");
  return `${elevated}:${mods}`;
}
